import { Fragment } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

const formatMoney = (v) => '$' + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CashFlowTable({ cashFlow, cfExpandedSection, setCfExpandedSection }) {
  if (!cashFlow) return null;
  const cf = cashFlow;

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
        Cash Flow Statement
        {cf.match
          ? <span className="text-emerald-400 text-[10px] font-normal flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Cash End Verified</span>
          : <span className="text-amber-400 text-[10px] font-normal flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Cash End Mismatch</span>
        }
      </h3>
      <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
        {/* Section comparison table */}
        <div className="bg-gray-800/20 rounded-lg overflow-hidden mb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-3 py-1.5 text-left text-[10px] text-gray-500 uppercase">Section</th>
                <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">QB</th>
                <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">FinBoard</th>
                <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">Variance</th>
              </tr>
            </thead>
            <tbody>
              {/* Net Income */}
              <tr className="border-b border-gray-800/30 bg-gray-800/10">
                <td className="px-3 py-1.5 text-gray-300 font-medium">Net Income</td>
                <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(cf.net_income_qb)}</td>
                <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(cf.net_income_fb)}</td>
                <td className={`px-3 py-1.5 text-right font-mono ${
                  Math.abs(cf.net_income_fb - cf.net_income_qb) <= 1 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {Math.abs(cf.net_income_fb - cf.net_income_qb) <= 1 ? '✓' : formatMoney(cf.net_income_fb - cf.net_income_qb)}
                </td>
              </tr>
              {cf.categories.map(c => (
                <Fragment key={c.category}>
                  <tr
                    className="border-b border-gray-800/30 cursor-pointer hover:bg-gray-800/30"
                    onClick={() => setCfExpandedSection(cfExpandedSection === c.category ? null : c.category)}
                  >
                    <td className="px-3 py-1.5 text-gray-300 flex items-center gap-1">
                      {c.line_items && c.line_items.length > 0 && (
                        <span className="text-gray-600 text-[10px]">{cfExpandedSection === c.category ? '▼' : '▶'}</span>
                      )}
                      {c.category} Activities
                    </td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(c.qb_total)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(c.fb_total)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${c.match ? 'text-emerald-400' : 'text-red-400'}`}>
                      {c.match ? '✓' : ''} {c.variance >= 0 ? '+' : ''}{formatMoney(c.variance)}
                    </td>
                  </tr>
                  {cfExpandedSection === c.category && c.line_items && c.line_items.map((li, i) => (
                    <tr key={i} className="border-b border-gray-800/10 bg-gray-800/10">
                      <td className="px-3 py-1 pl-8 text-gray-500 text-[11px] truncate max-w-[180px]">{li.name}</td>
                      <td className="px-3 py-1 text-right text-gray-500 font-mono text-[11px]">
                        {li.qb_amount != null ? formatMoney(li.qb_amount) : '—'}
                      </td>
                      <td className="px-3 py-1 text-right text-gray-500 font-mono text-[11px]">
                        {li.fb_amount != null ? formatMoney(li.fb_amount) : '—'}
                      </td>
                      <td className={`px-3 py-1 text-right font-mono text-[11px] ${li.match ? 'text-emerald-400/60' : 'text-red-400'}`}>
                        {li.match ? '✓' : formatMoney(li.variance)}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {/* Net cash change */}
              <tr className="border-t border-gray-700">
                <td className="px-3 py-1.5 text-gray-200 font-medium">Net Cash Change</td>
                <td className="px-3 py-1.5 text-right text-gray-300 font-mono font-medium">{formatMoney(cf.net_cash_change_qb)}</td>
                <td className="px-3 py-1.5 text-right text-gray-300 font-mono font-medium">{formatMoney(cf.net_cash_change_fb)}</td>
                <td className={`px-3 py-1.5 text-right font-mono font-medium ${
                  Math.abs(cf.net_cash_change_fb - cf.net_cash_change_qb) <= 1 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {Math.abs(cf.net_cash_change_fb - cf.net_cash_change_qb) <= 1 ? '✓' : formatMoney(cf.net_cash_change_fb - cf.net_cash_change_qb)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Cash beginning / end with verification */}
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="bg-gray-800/30 border border-gray-800 rounded-lg px-3 py-2">
            <div className="text-gray-500">Cash Beginning</div>
            <div className="text-gray-200 font-mono mt-0.5">{formatMoney(cf.cash_beginning_qb)}</div>
          </div>
          <div className="bg-gray-800/30 border border-gray-800 rounded-lg px-3 py-2">
            <div className="text-gray-500">Cash End (QB)</div>
            <div className="text-gray-200 font-mono mt-0.5">{formatMoney(cf.cash_end_qb)}</div>
          </div>
          <div className={`rounded-lg px-3 py-2 border ${
            cf.match ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-amber-500/5 border-amber-500/10'
          }`}>
            <div className="text-gray-500">Cash End (FinBoard)</div>
            <div className={`font-mono mt-0.5 ${cf.match ? 'text-emerald-400' : 'text-amber-400'}`}>
              {formatMoney(cf.cash_end_fb)}
            </div>
            {cf.cash_end_variance !== 0 && (
              <div className={`text-[10px] mt-0.5 ${cf.match ? 'text-emerald-400/60' : 'text-amber-400/60'}`}>
                Var: {formatMoney(cf.cash_end_variance)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
